import { format, fromUnixTime } from 'date-fns';
import { get } from 'lodash';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import type {
    DatePeriodData,
    MonthlyRecurrenceData,
    TimeOffset,
    WeeklyRecurrenceData,
    YearlyRecurrenceData,
} from '@travelity/api/generated';
import type { PackageFormValues, PackageStepValue } from './add-package.types';

export const getFieldsForStep = (
    step: PackageStepValue,
    values: PackageFormValues
): string[] => {
    switch (step) {
        case 'product-details':
            return ['productDetails.name', 'productDetails.description'];
        case 'unit-products':
            return values.products.flatMap((product, index) => {
                const dayField = `products.${index}.day_index`;
                const startTimeFields = (product.items || []).map(
                    (_, itemIndex) =>
                        `products.${index}.items.${itemIndex}.start_time`
                );
                return [dayField, ...startTimeFields];
            });
        case 'pricing':
            return ['discount', 'total'];
        case 'assets':
            return ['assets.images'];
        case 'preview':
        default:
            return [];
    }
};

export const getStepErrorMessages = (
    errors: FieldErrors<FieldValues>,
    fields: string[]
): string[] => {
    return fields
        .map(field => {
            const message = get(errors, field)?.message;
            return typeof message === 'string' ? message : undefined;
        })
        .filter(Boolean) as string[];
};

const toOrdinal = (n: number): string => {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
};

const joinWithAnd = (items: string[]): string => {
    if (items.length <= 1) return items[0] ?? '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const uniqSortedNums = (xs: number[]): number[] =>
    [...new Set(xs)].sort((a, b) => a - b);

const formatWeekly = (recurrence: WeeklyRecurrenceData): string => {
    const days = recurrence.days_of_week;
    if (days.length === 0) return 'Every week';
    if (days.length === 7) return 'Every day';
    return `${joinWithAnd(days)}`;
};

const formatMonthly = (recurrence: MonthlyRecurrenceData): string => {
    const days = uniqSortedNums(recurrence.days_of_month).map(toOrdinal);
    if (days.length === 0) return 'Every month';
    return `${joinWithAnd(days)}`;
};

const dedupeStable = <T>(xs: T[]): T[] => {
    const seen = new Set<T>();
    const out: T[] = [];
    for (const x of xs) {
        if (seen.has(x)) continue;
        seen.add(x);
        out.push(x);
    }
    return out;
};

const pickYearlyUnixSeconds = (
    recurrence: YearlyRecurrenceData,
    dates: Array<DatePeriodData>
): number[] => {
    const fromRecurrence = recurrence.dates.filter((v): v is number =>
        Number.isFinite(v)
    );
    if (fromRecurrence.length > 0) return dedupeStable(fromRecurrence);

    const fromDates = dates
        .map(d => d.start)
        .filter((v): v is number => Number.isFinite(v));

    if (fromDates.length > 0) return dedupeStable(fromDates);

    return [];
};

const formatYearly = (
    recurrence: YearlyRecurrenceData,
    dates: Array<DatePeriodData>
): string => {
    const candidatesUnixSeconds = pickYearlyUnixSeconds(recurrence, dates);
    if (candidatesUnixSeconds.length === 0) return 'Every year';

    const monthMap = new Map<number, Set<number>>();

    candidatesUnixSeconds.forEach(seconds => {
        const date = fromUnixTime(seconds);
        const month = date.getMonth();
        const day = date.getDate();
        const existing = monthMap.get(month);
        if (existing) {
            existing.add(day);
        } else {
            monthMap.set(month, new Set([day]));
        }
    });

    const monthLabels = Array.from(monthMap.entries())
        .sort(([a], [b]) => a - b)
        .map(([month, daysSet]) => {
            const days = Array.from(daysSet).sort((a, b) => a - b);
            const monthLabel = format(new Date(2000, month, 1), 'MMM');
            return `${monthLabel} ${days.join(', ')}`;
        });

    return `Every ${joinWithAnd(monthLabels)}`;
};

export const getScheduleRecurrenceLabel = (
    recurrence:
        | MonthlyRecurrenceData
        | WeeklyRecurrenceData
        | YearlyRecurrenceData
        | undefined,
    dates: Array<DatePeriodData>
): string => {
    if (!recurrence) return '';

    if (recurrence.type === 'weekly') {
        return formatWeekly(recurrence);
    }

    if (recurrence.type === 'monthly') {
        return formatMonthly(recurrence);
    }

    return formatYearly(recurrence, dates);
};

export const parseHHmm = (value: string): TimeOffset => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim())!;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    return { hours, minutes };
};
